import type { JSX, ReactNode } from 'react'

/**
 * SectionHeader — the standard heading block for a form section:
 * an optional accent icon, a small uppercase eyebrow, a title, a description,
 * and an optional actions slot aligned to the right.
 */
export function SectionHeader({
  eyebrow,
  title,
  description,
  icon,
  actions,
  className = '',
}: {
  eyebrow?: string | undefined
  title?: string | undefined
  description?: string | undefined
  icon?: string | undefined
  actions?: ReactNode | undefined
  className?: string | undefined
}): JSX.Element {
  return (
    <div className={`flex items-start justify-between gap-4 ${className}`}>
      <div className="flex items-start gap-3.5">
        {icon && (
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[3px] bg-[var(--kc3-accent)]/10 text-[var(--kc3-accent)]">
            <i className={`${icon} text-lg`} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          {eyebrow && (
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--kc3-accent)]">
              {eyebrow}
            </span>
          )}
          {title && <h2 className="mt-0.5 text-[17px] font-bold text-gray-900">{title}</h2>}
          {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </div>
  )
}

/**
 * FormSection — a titled card: a `SectionHeader` on a subtle header strip,
 * followed by a padded body. The primary building block for grouping fields
 * into visually distinct, easy-to-scan cards.
 */
export function FormSection({
  eyebrow,
  title,
  description,
  icon,
  actions,
  children,
  bodyClassName = '',
  className = '',
}: {
  eyebrow?: string | undefined
  title?: string | undefined
  description?: string | undefined
  icon?: string | undefined
  actions?: ReactNode | undefined
  children: ReactNode
  bodyClassName?: string | undefined
  className?: string | undefined
}): JSX.Element {
  const hasHeader = Boolean(eyebrow || title || description || icon || actions)
  return (
    <section
      className={`overflow-hidden rounded-[3px] border border-gray-200 bg-white shadow-sm ${className}`}
    >
      {hasHeader && (
        <div className="border-b border-gray-100 bg-gray-50/50 px-6 py-5">
          <SectionHeader
            eyebrow={eyebrow}
            title={title}
            description={description}
            icon={icon}
            actions={actions}
          />
        </div>
      )}
      <div className={`p-6 ${bodyClassName}`}>{children}</div>
    </section>
  )
}

type InfoTone = 'default' | 'info' | 'warning' | 'success'

const TONE_STYLES: Record<InfoTone, { wrap: string; icon: string }> = {
  default: { wrap: 'border-gray-200 bg-gray-50 text-gray-700', icon: 'text-gray-400' },
  info: { wrap: 'border-blue-200 bg-blue-50 text-blue-800', icon: 'text-blue-500' },
  warning: { wrap: 'border-amber-200 bg-amber-50 text-amber-800', icon: 'text-amber-500' },
  success: { wrap: 'border-green-200 bg-green-50 text-green-800', icon: 'text-green-500' },
}

/**
 * InfoCard — a compact, tonal callout for grouped settings, notes and warnings.
 * Renders an optional leading icon, a title, and body content.
 */
export function InfoCard({
  icon,
  title,
  tone = 'default',
  children,
  className = '',
}: {
  icon?: string | undefined
  title?: string | undefined
  tone?: InfoTone | undefined
  children?: ReactNode | undefined
  className?: string | undefined
}): JSX.Element {
  const styles = TONE_STYLES[tone]
  return (
    <div className={`flex items-start gap-3 rounded-[3px] border p-4 ${styles.wrap} ${className}`}>
      {icon && <i className={`${icon} mt-0.5 shrink-0 ${styles.icon}`} aria-hidden="true" />}
      <div className="min-w-0 text-sm">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? 'mt-1' : ''}>{children}</div>}
      </div>
    </div>
  )
}
